/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useState } from 'react';

import { Button } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsDownIcon, ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { Product } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import FeedbackDialog from 'bundles/skillset-learner-page/private/subcomponents/feedback/FeedbackDialog';
import useFeedbackAPI from 'bundles/skillset-learner-page/private/subcomponents/feedback/useFeedbackAPI';

import _t from 'i18n!nls/skillset-learner-page';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  cardButton: css`
    margin-left: auto;
    display: flex;
    padding: var(--cds-spacing-150) var(--cds-spacing-100);
  `,
  buttonContainer: css`
    display: flex;
  `,
};

type Props = {
  onFeedbackSent: (productId: string, feedback: 'Yes' | 'No' | '') => void;
  userId: number;
  product: Product;
  trackingData: { [key: string]: any };
  isDarkCard: boolean;
  skillName: string;
  contentType: string;
  feedbackContext: string;
  previousFeedback?: 'Yes' | 'Hidden' | 'No' | '';
};

type FEEDBACK_STATE = 'NOT_SHOWN' | 'FEEDBACK_DIALOG';

const stopPropagation = (e: { stopPropagation: () => void }) => e.stopPropagation();

const FeedbackButton = ({
  isDarkCard,
  product,
  skillName,
  contentType,
  trackingData,
  previousFeedback,
  userId,
  feedbackContext,
  onFeedbackSent,
}: Props) => {
  const [state, setState] = useState<FEEDBACK_STATE>('NOT_SHOWN');
  const close = useCallback<() => $TSFixMe>(() => setState('NOT_SHOWN'), []);
  const { sendFeedback } = useFeedbackAPI({ userId, trackingData, product, feedbackContext, onFeedbackSent });

  return (
    // we need to stop the propagation so this elements don't open up the course modals
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={stopPropagation} onKeyPress={stopPropagation} onClick={stopPropagation}>
      {previousFeedback && (
        <TrackedButton
          {...Button.defaultProps}
          trackingName="clear_old_feedback"
          trackingData={trackingData}
          aria-label={_t('Remove Feedback')}
          variant={isDarkCard ? 'ghostInvert' : 'ghost'}
          css={styles.cardButton}
          onClick={() => {
            sendFeedback({
              prompt: 'Agree with the recommendation',
              feedback: '',
            });
          }}
          role="button"
        >
          {previousFeedback === 'Yes' && <ThumbsUpFilledIcon data-testid="thumbs-up-filled-icon" />}
          {(previousFeedback === 'No' || previousFeedback === 'Hidden') && (
            <ThumbsDownFilledIcon data-testid="thumbs-down-filled-icon" />
          )}
        </TrackedButton>
      )}
      {!previousFeedback && (
        <div css={styles.buttonContainer}>
          <TrackedButton
            {...Button.defaultProps}
            trackingName="agree_with_rec"
            trackingData={trackingData}
            aria-label={_t('Agree with recommendation')}
            variant={isDarkCard ? 'ghostInvert' : 'ghost'}
            css={styles.cardButton}
            onClick={() => {
              sendFeedback({
                prompt: 'Agree with the recommendation',
                feedback: 'Yes',
              });
            }}
          >
            <ThumbsUpIcon />
          </TrackedButton>
          <TrackedButton
            {...Button.defaultProps}
            trackingName="disagree_with_rec"
            trackingData={trackingData}
            aria-label={_t('Disagree with recommendation')}
            variant={isDarkCard ? 'ghostInvert' : 'ghost'}
            css={styles.cardButton}
            onClick={() => setState('FEEDBACK_DIALOG')}
          >
            <ThumbsDownIcon />
          </TrackedButton>
        </div>
      )}

      <FeedbackDialog
        isOpen={state === 'FEEDBACK_DIALOG'}
        onClose={close}
        trackingData={trackingData}
        sendFeedback={(reason) => {
          sendFeedback({
            prompt: 'Agree with the recommendation',
            feedback: 'No',
            negativeFeedbackReason: reason,
          });
          close();
        }}
        prompt={_t("Why did you want to see less recommendations like '#{productName}'?", {
          productName: product.name,
        })}
        options={[
          _t('This #{contentType} is not relevant for the skill "#{skillName}"', {
            contentType,
            skillName,
          }),
          _t('This #{contentType} is too basic', { contentType }),
          _t('This #{contentType} is too advanced', { contentType }),
          _t("This #{contentType} is too broad - I'm looking to learn a more specific skill", {
            contentType,
          }),
          _t("This #{contentType} is too specific - I'm looking to build a broad foundation", {
            contentType,
          }),
        ]}
      />
    </div>
  );
};

export { FeedbackButton, stopPropagation };
export default FeedbackButton;
